import { Search, SlidersHorizontal, PlusSquare, Heart } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { memo, useMemo, startTransition, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { usePrefetchFavorites } from "../hooks/useFavorites";
import { usePrefetchListings } from "../hooks/useListings";

const navItemsConfig = [
  { id: "discover", path: "/discover", icon: Search, labelKey: "nav.discover" },
  { id: "preferences", path: "/preferences", icon: SlidersHorizontal, labelKey: "nav.filters" },
  { id: "favorites", path: "/favorites", icon: Heart, labelKey: "nav.favorites" },
  { id: "listings", path: "/listings", icon: PlusSquare, labelKey: "nav.listings" },
  ];

// Memoized navigation item component
const NavItem = memo(({ item, isActive, onClick, onPrefetch, label }: {
  item: typeof navItemsConfig[0];
  isActive: boolean;
  onClick: () => void;
  onPrefetch?: () => void;
  label: string;
}) => {
  const Icon = item.icon;
  
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => onPrefetch?.()}  // Desktop: prefetch on hover
      onTouchStart={() => onPrefetch?.()} // Mobile: prefetch on touch start
      className={`flex flex-col items-center gap-1 px-4 py-1 rounded-xl transition-all ${
        isActive
          ? "text-purple-600"
          : "text-gray-500 hover:text-gray-700"
      }`}
      aria-label={label}
      aria-current={isActive ? "page" : undefined}
    >
      <Icon className={`w-6 h-6 ${isActive ? "fill-purple-100" : ""}`} />
      <span className="text-xs">{label}</span>
    </button>
  );
});

NavItem.displayName = 'NavItem';

export const BottomNav = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Prefetch hooks - warm cache before navigation
  const prefetchFavorites = usePrefetchFavorites();
  const prefetchListings = usePrefetchListings();
  
  // Track if prefetch already fired (don't spam the API)
  const prefetchedRef = useRef<Set<string>>(new Set());

  // Memoize active ID calculation
  const activeId = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/listings')) return 'listings';
    if (path.startsWith('/favorites')) return 'favorites';
    if (path.startsWith('/preferences')) return 'preferences';
    if (path.startsWith('/discover')) return 'discover';
    return 'discover';
  }, [location.pathname]);

  // Use startTransition for non-urgent navigation
  const handleNavClick = (path: string) => {
    startTransition(() => {
      navigate(path);
    });
  };
  
  // Create prefetch handlers for each nav item
  const getPrefetchHandler = useCallback((itemId: string) => {
    // Only listings and favorites benefit from prefetch
    if (itemId !== 'listings' && itemId !== 'favorites') {
      return undefined;
    }
    
    return () => {
      // Don't re-prefetch if already done in this session
      if (prefetchedRef.current.has(itemId)) return;
      prefetchedRef.current.add(itemId);
      
      // Fire and forget - don't await
      if (itemId === 'listings') {
        prefetchListings();
      } else if (itemId === 'favorites') {
        prefetchFavorites();
      }
    };
  }, [prefetchFavorites, prefetchListings]);

  return (
    <div 
      className="bg-white border-t border-gray-200 fixed-stable"
      style={{
        // Hardware acceleration to prevent bounce
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        // Prevent any scroll/touch interaction issues
        touchAction: 'none',
      }}
    >
      <div className="flex items-center justify-around px-2 py-3">
        {navItemsConfig.map((item) => (
          <NavItem
            key={item.id}
            item={item}
            isActive={activeId === item.id}
            onClick={() => handleNavClick(item.path)}
            onPrefetch={getPrefetchHandler(item.id)}
            label={t(item.labelKey)}
          />
        ))}
      </div>
    </div>
  );
});

BottomNav.displayName = 'BottomNav';